import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import SecurityIcon from '@mui/icons-material/Security';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useBotDefense } from '../../hooks/useBotDefense';

// Helper: detects if a background string represents a dark color
const isDarkColor = (color) => {
  if (!color || typeof color !== 'string') return true;
  const c = color.trim().toLowerCase();
  if (
    c.includes('18, 18, 18') ||
    c.includes('30, 41, 59') ||
    c.includes('15, 23, 42') ||
    c.includes('10, 14, 23') ||
    c.includes('11, 24, 39') ||
    c.includes('17, 24, 39') ||
    c === '#000' ||
    c === '#000000' ||
    c === '#111827' ||
    c === '#121212' ||
    c === '#1e1e1e' ||
    c === '#1e293b' ||
    c === '#0f172a' ||
    c === '#0a0e17' ||
    c === '#0b0f19'
  ) return true;
  if (c.startsWith('#')) {
    const hex = c.replace('#', '');
    let r = 0, g = 0, b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  const rgbMatch = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  return false;
};

const Newsletter = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { t, newsletterUi } = useLanguage();
  const { executeBotDefense, isBotDefenseActive, provider } = useBotDefense();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message: string }
  const formLoadedAtRef = useRef(Date.now());

  // Configurable UI settings from Dashboard (with pristine ITFORSEC defaults and light mode adaptability)
  const ui = newsletterUi || {};
  const bgColor = isDarkMode
    ? (ui.bg_color || 'rgb(18, 18, 18)')
    : (ui.bg_color_light || (isDarkColor(ui.bg_color) ? '#ffffff' : (ui.bg_color || '#ffffff')));
  const cardStyle = ui.card_style || 'cyber-glow';
  const accentGradientType = ui.accent_gradient || 'itforsec';
  const badgeIconType = ui.badge_icon || 'shield';
  const showBadge = ui.show_badge !== false;
  const showPerks = ui.show_perks !== false;
  const showSubCount = ui.show_subscriber_count !== false;
  const subCountVal = ui.subscriber_count || '1,200+';
  const cardLayout = ui.card_layout || 'centered'; // 'centered' or 'split'
  const showBotBadge = ui.show_bot_badge !== false;

  // Gradients based on dashboard selection
  const gradientMap = {
    itforsec: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
    'cyan-indigo': 'linear-gradient(135deg, #06B6D4 0%, #6366F1 100%)',
    'purple-emerald': 'linear-gradient(135deg, #8B5CF6 0%, #10B981 100%)',
    'gold-amber': 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  };
  const activeGradient = gradientMap[accentGradientType] || gradientMap.itforsec;

  // Icon mapping for badge
  const renderBadgeIcon = () => {
    const iconProps = { sx: { fontSize: 17 } };
    switch (badgeIconType) {
      case 'mail': return <MailOutlineIcon {...iconProps} />;
      case 'rocket': return <RocketLaunchOutlinedIcon {...iconProps} />;
      case 'lightning': return <BoltOutlinedIcon {...iconProps} />;
      case 'lock': return <LockOutlinedIcon {...iconProps} />;
      case 'shield':
      default:
        return <ShieldOutlinedIcon {...iconProps} />;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setFeedback({ type: 'error', message: t('emailInvalid') || 'Please enter a valid email address.' });
      return;
    }

    setLoading(true);
    setFeedback(null);

    // 🛡️ Invisible Bot Defense Token Retrieval (Zero Puzzles)
    let botToken = null;
    if (isBotDefenseActive) {
      try {
        botToken = await executeBotDefense('newsletter');
      } catch (e) {
        // Fallback gracefully on script failure
      }
    }

    try {
      const response = await fetch('/api/newsletter.php?action=subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          bot_token: botToken,
          hp_newsletter: '', // honeypot
          form_timing: Date.now() - formLoadedAtRef.current
        })
      });

      const data = await response.json();
      if (data.success) {
        setFeedback({
          type: 'success',
          message: data.message || t('newsletterSuccess') || 'Thank you for subscribing to ITFORSEC briefings!'
        });
        setEmail('');
      } else {
        setFeedback({
          type: 'error',
          message: data.error || 'Failed to subscribe. Please try again.'
        });
      }
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Network error. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Perks list
  const perks = [
    t('newsletterPerk1') || 'Zero Spam Policy',
    t('newsletterPerk2') || 'Weekly Threat Intel',
    t('newsletterPerk3') || '1-Click Unsubscribe'
  ];

  // Dynamic card box styling based on dashboard card_style
  const getCardSx = () => {
    const base = {
      p: { xs: 4, sm: 5, md: cardLayout === 'split' ? 6 : 6 },
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
    };

    if (cardStyle === 'glassmorphism') {
      return {
        ...base,
        background: isDarkMode ? alpha(bgColor.startsWith('#') ? bgColor : '#121212', 0.85) : alpha(bgColor, 0.88),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(46, 91, 255, 0.12)'}`,
        boxShadow: isDarkMode ? '0 20px 50px rgba(0,0,0,0.5)' : '0 20px 45px rgba(46, 91, 255, 0.1), 0 4px 12px rgba(0,0,0,0.03)',
      };
    }

    if (cardStyle === 'accent-banner') {
      return {
        ...base,
        background: isDarkMode
          ? `linear-gradient(135deg, rgba(46,91,255,0.14) 0%, rgba(0,200,150,0.08) 100%), ${bgColor}`
          : `linear-gradient(135deg, rgba(46,91,255,0.08) 0%, rgba(0,200,150,0.06) 100%), ${bgColor}`,
        border: `2px solid ${alpha('#2E5BFF', isDarkMode ? 0.35 : 0.25)}`,
        boxShadow: isDarkMode
          ? `0 20px 50px rgba(0,0,0,0.4), 0 0 25px ${alpha('#2E5BFF', 0.2)}`
          : `0 16px 40px rgba(46, 91, 255, 0.12), 0 0 20px ${alpha('#2E5BFF', 0.15)}`,
      };
    }

    if (cardStyle === 'minimal-tech') {
      return {
        ...base,
        background: bgColor,
        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 25px rgba(0,0,0,0.04)',
      };
    }

    // Default: 'cyber-glow'
    return {
      ...base,
      background: isDarkMode
        ? `radial-gradient(circle at 50% 0%, ${alpha('#2E5BFF', 0.16)} 0%, ${alpha('#00C896', 0.07)} 40%, ${bgColor} 90%)`
        : `radial-gradient(circle at 50% 0%, ${alpha('#2E5BFF', 0.08)} 0%, ${alpha('#00C896', 0.04)} 40%, ${bgColor} 90%)`,
      backdropFilter: 'blur(16px)',
      border: `1px solid ${isDarkMode ? 'rgba(46, 91, 255, 0.3)' : 'rgba(46, 91, 255, 0.16)'}`,
      boxShadow: isDarkMode
        ? `0 24px 60px rgba(0, 0, 0, 0.6), 0 0 35px ${alpha('#2E5BFF', 0.15)}, inset 0 1px 0 rgba(255,255,255,0.1)`
        : `0 20px 45px rgba(46, 91, 255, 0.1), 0 4px 12px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9)`,
    };
  };

  return (
    <Box
      id="newsletter"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode
          ? bgColor
          : (ui.section_bg_light || `linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F1F5F9 100%)`),
        borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'}`,
        borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'}`
      }}
    >
      {/* Ambient background light spheres */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          width: { xs: '260px', md: '500px' },
          height: { xs: '260px', md: '500px' },
          background: `radial-gradient(circle, ${alpha('#2E5BFF', isDarkMode ? 0.16 : 0.08)} 0%, transparent 65%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: { xs: '240px', md: '450px' },
          height: { xs: '240px', md: '450px' },
          background: `radial-gradient(circle, ${alpha('#00C896', isDarkMode ? 0.12 : 0.06)} 0%, transparent 65%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <Container maxWidth={cardLayout === 'split' ? 'lg' : 'md'} sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={getCardSx()}>
            {/* Top Glowing Accent Line */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: activeGradient,
              }}
            />

            {cardLayout === 'split' ? (
              // SPLIT 2-COLUMN LAYOUT
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
                  gap: { xs: 4, md: 6 },
                  alignItems: 'center',
                }}
              >
                {/* Left Column: Heading & Perks */}
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  {showBadge && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        borderRadius: '50px',
                        background: isDarkMode ? 'rgba(46, 91, 255, 0.14)' : 'rgba(46, 91, 255, 0.08)',
                        border: '1px solid rgba(46, 91, 255, 0.3)',
                        color: isDarkMode ? '#60a5fa' : '#2E5BFF',
                        mb: 2.5
                      }}
                    >
                      {renderBadgeIcon()}
                      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                        {t('newsletterBadge') || 'Cybersecurity & Tech Briefings'}
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.75rem', sm: '2.1rem', md: '2.4rem' },
                      color: theme.palette.text.primary,
                      mb: 1.5,
                      lineHeight: 1.25,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {t('newsletterTitle') || 'Stay Ahead in Tech & Cyber Security'}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                      fontSize: { xs: '0.95rem', md: '1.02rem' },
                      lineHeight: 1.6
                    }}
                  >
                    {t('newsletterSubtitle') || 'Subscribe to our newsletter for exclusive security briefings, system optimization guides, and technology insights.'}
                  </Typography>

                  {showPerks && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                      {perks.map((p, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <CheckCircleOutlineIcon sx={{ color: '#00C896', fontSize: 16 }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem' }}>
                            {p}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Right Column: Form Box */}
                <Box>
                  {showSubCount && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.75,
                        py: 0.5,
                        borderRadius: '20px',
                        background: isDarkMode ? 'rgba(0, 200, 150, 0.12)' : 'rgba(0, 200, 150, 0.1)',
                        border: '1px solid rgba(0, 200, 150, 0.25)',
                        color: '#00C896',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        mb: 2.5
                      }}
                    >
                      <span>👥</span>
                      <span>{t('newsletterSubCount') || `Join ${subCountVal} IT leaders & security specialists`}</span>
                    </Box>
                  )}

                  {feedback && (
                    <Alert
                      severity={feedback.type}
                      sx={{ mb: 2.5, borderRadius: 2 }}
                      onClose={() => setFeedback(null)}
                    >
                      {feedback.message}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('newsletterPlaceholder') || 'Enter your business email...'}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <MarkEmailReadOutlinedIcon
                            sx={{ color: theme.palette.text.secondary, mr: 1, fontSize: 20 }}
                          />
                        ),
                        sx: {
                          borderRadius: 2.5,
                          backgroundColor: isDarkMode ? alpha('#0b0f19', 0.8) : '#ffffff',
                          '& fieldset': {
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
                          },
                          '&:hover fieldset': {
                            borderColor: '#2E5BFF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2E5BFF',
                            borderWidth: '2px'
                          }
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      endIcon={loading ? null : <SendIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        py: 1.6,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: activeGradient,
                        color: '#ffffff',
                        boxShadow: '0 6px 20px rgba(46, 91, 255, 0.35)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          background: activeGradient,
                          boxShadow: '0 10px 28px rgba(0, 200, 150, 0.45)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t('newsletterButton') || 'Subscribe to Briefings'
                      )}
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, opacity: 0.8, fontSize: '0.75rem' }}>
                      {t('newsletterDisclaimer') || '🔒 Zero spam guarantee. Unsubscribe at any time with 1 click.'}
                    </Typography>

                    {showBotBadge && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SecurityIcon sx={{ fontSize: 13, color: '#00C896' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary, opacity: 0.8 }}>
                          {isBotDefenseActive ? `Protected by ${provider === 'turnstile' ? 'Cloudflare Turnstile' : 'reCAPTCHA v3'}` : 'Protected by Anti-Spam'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              // CENTERED SINGLE-COLUMN LAYOUT (Default & High-Focus)
              <Box sx={{ textAlign: 'center', maxWidth: '640px', mx: 'auto' }}>
                {showBadge && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.75,
                      borderRadius: '50px',
                      background: isDarkMode ? 'rgba(46, 91, 255, 0.14)' : 'rgba(46, 91, 255, 0.08)',
                      border: '1px solid rgba(46, 91, 255, 0.3)',
                      color: isDarkMode ? '#60a5fa' : '#2E5BFF',
                      mb: 2.5
                    }}
                  >
                    {renderBadgeIcon()}
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {t('newsletterBadge') || 'Cybersecurity & Tech Briefings'}
                    </Typography>
                  </Box>
                )}

                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                    color: theme.palette.text.primary,
                    mb: 1.5,
                    lineHeight: 1.25,
                    letterSpacing: '-0.5px'
                  }}
                >
                  {t('newsletterTitle') || 'Stay Ahead in Tech & Cyber Security'}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    maxWidth: '580px',
                    mx: 'auto',
                    mb: 3,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.6
                  }}
                >
                  {t('newsletterSubtitle') || 'Subscribe to our newsletter for exclusive security briefings, system optimization guides, and technology insights.'}
                </Typography>

                {showSubCount && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.6,
                      borderRadius: '20px',
                      background: isDarkMode ? 'rgba(0, 200, 150, 0.12)' : 'rgba(0, 200, 150, 0.08)',
                      border: '1px solid rgba(0, 200, 150, 0.25)',
                      color: '#00C896',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      mb: 3
                    }}
                  >
                    <span>👥</span>
                    <span>{t('newsletterSubCount') || `Join ${subCountVal} IT leaders & security specialists`}</span>
                  </Box>
                )}

                {feedback && (
                  <Alert
                    severity={feedback.type}
                    sx={{
                      maxWidth: '540px',
                      mx: 'auto',
                      mb: 3,
                      borderRadius: 2,
                      textAlign: 'left'
                    }}
                    onClose={() => setFeedback(null)}
                  >
                    {feedback.message}
                  </Alert>
                )}

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    maxWidth: '540px',
                    mx: 'auto'
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('newsletterPlaceholder') || 'Enter your business email address...'}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <MarkEmailReadOutlinedIcon
                          sx={{ color: theme.palette.text.secondary, mr: 1, fontSize: 20 }}
                        />
                      ),
                      sx: {
                        borderRadius: 2.5,
                        backgroundColor: isDarkMode ? alpha('#0b0f19', 0.8) : '#ffffff',
                        '& fieldset': {
                          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
                        },
                        '&:hover fieldset': {
                          borderColor: '#2E5BFF'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2E5BFF',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    endIcon={loading ? null : <SendIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      px: 3.5,
                      py: { xs: 1.5, sm: 'auto' },
                      borderRadius: 2.5,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      background: activeGradient,
                      color: '#ffffff',
                      boxShadow: '0 6px 20px rgba(46, 91, 255, 0.35)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        background: activeGradient,
                        boxShadow: '0 10px 28px rgba(0, 200, 150, 0.45)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('newsletterButton') || 'Subscribe to Briefings'
                    )}
                  </Button>
                </Box>

                {showPerks && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      gap: { xs: 2, sm: 3 },
                      mt: 3.5
                    }}
                  >
                    {perks.map((p, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#00C896', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem' }}>
                          {p}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mt: 2.5
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      opacity: 0.8,
                      fontSize: '0.75rem'
                    }}
                  >
                    {t('newsletterDisclaimer') || '🔒 Zero spam guarantee. Unsubscribe at any time with a single click.'}
                  </Typography>

                  {showBotBadge && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1, borderLeft: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
                      <SecurityIcon sx={{ fontSize: 13, color: '#00C896' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary, opacity: 0.8 }}>
                        {isBotDefenseActive ? `Protected by ${provider === 'turnstile' ? 'Cloudflare Turnstile' : 'reCAPTCHA v3'}` : 'Invisible Anti-Spam Active'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Newsletter;
